import * as React from 'react';
import { Pressable, type PressableProps } from './pressable';

/**
 * Project OptionRow.
 *
 * The full width selectable row used by the multi-select onboarding screens.
 * Two states:
 *
 * - unselected: white fill, 1px Cement border, Figtree Regular
 * - selected:   Seafoam fill, faint Evergreen border, Figtree SemiBold
 *
 * Geometry follows the prototype `.option-row` (12px radius, 15/16 padding,
 * 15px text, and a hairline shadow), which is the layout authority. The Figma
 * frame draws the same row at an 8px radius with 16px text; its colours are
 * what we take from it. Stack these in a `VStack` with `gap={2}`.
 */
export type OptionRowProps = Omit<PressableProps, 'children'> & {
  isSelected?: boolean;
  children: React.ReactNode;
};

export const OptionRow = React.forwardRef<HTMLButtonElement, OptionRowProps>(
  ({ isSelected = false, children, ...rest }, ref) => (
    <Pressable
      ref={ref}
      aria-pressed={isSelected}
      w="100%"
      px="16px"
      py="15px"
      rounded="row"
      borderWidth="1px"
      flexDirection="row"
      alignItems="center"
      justifyContent="center"
      fontFamily="body"
      fontSize="15px"
      lineHeight="1.4"
      boxShadow="0 1px 2px rgba(3, 51, 56, 0.05)"
      bg={isSelected ? 'secondary.200' : 'white'}
      borderColor={isSelected ? 'secondary.200' : 'cement.200'}
      color={isSelected ? 'white' : 'secondary.500'}
      fontWeight={isSelected ? 600 : 400}
      _hover={isSelected ? undefined : { borderColor: 'secondary.200' }}
      {...rest}
    >
      {children}
    </Pressable>
  )
);
OptionRow.displayName = 'OptionRow';
